import React, { useState } from 'react';
import { getWidgetIconById, widgetIconOptions } from './widgetIcons';

const EMPTY_PAGE_JSON = JSON.stringify({ name: '', id: '', widgets: [] });

function ReportSideMenu({
	pages = [],
	activeReportPageOid = null,
	loading = false,
	loadError = null,
	onSelect = () => {},
	onCreated,
	onUpdated,
	onCreatePage,
	onUpdatePage,
}) {
	const [isOpen, setIsOpen] = useState(true);
	const [formMode, setFormMode] = useState(null);
	const [editOid, setEditOid] = useState(null);
	const [newName, setNewName] = useState('');
	const [newDescription, setNewDescription] = useState('');
	const [newIcon, setNewIcon] = useState('');
	const [iconPickerOpen, setIconPickerOpen] = useState(false);
	const [templateOid, setTemplateOid] = useState('');
	const [creating, setCreating] = useState(false);
	const [createError, setCreateError] = useState(null);
	const [hoveredReportTooltip, setHoveredReportTooltip] = useState(null);

	const showCollapsedTooltip = (element, name) => {
		if (isOpen) return;
		const rect = element.getBoundingClientRect();
		setHoveredReportTooltip({
			text: name || 'Untitled report',
			x: rect.left + rect.width / 2,
			y: rect.top - 8,
		});
	};

	const hideCollapsedTooltip = () => {
		setHoveredReportTooltip(null);
	};

	const openCreateForm = () => {
		setFormMode('create');
		setEditOid(null);
		setNewName('');
		setNewDescription('');
		setNewIcon('');
		setIconPickerOpen(false);
		setTemplateOid('');
		setCreateError(null);
	};

	const openEditForm = (page) => {
		if (!page.Oid) return;
		setFormMode('edit');
		setEditOid(page.Oid);
		setNewName(page.Name || '');
		setNewDescription(page.Description || '');
		setNewIcon(page.Icon || '');
		setIconPickerOpen(false);
		setTemplateOid('');
		setCreateError(null);
	};

	const closeForm = () => {
		setFormMode(null);
		setEditOid(null);
		setIconPickerOpen(false);
		setCreateError(null);
	};

	const handleCreateOrUpdateReportPage = async (event) => {
		event.preventDefault();
		const trimmedName = newName.trim();
		if (!trimmedName || creating) return;

		setCreating(true);
		setCreateError(null);

		try {
			if (formMode === 'create') {
				const templatePage = pages.find((page) => page.Oid === templateOid);
				const newPageJson = templatePage?.PageJson || EMPTY_PAGE_JSON;

				if (typeof onCreatePage === 'function') {
					const created = await onCreatePage({
						Name: trimmedName,
						Description: newDescription.trim() || undefined,
						Icon: newIcon || undefined,
						TemplateOid: templateOid || undefined,
						PageJson: newPageJson,
					});

					const createdOid = created?.Oid || created?.oid || (typeof created === 'string' ? created : null);
					if (createdOid) {
						onSelect(createdOid);
						if (typeof onCreated === 'function') {
							await onCreated(createdOid);
						}
					}
				}

				closeForm();
			} else if (formMode === 'edit') {
				if (!editOid) throw new Error('No report page selected for update.');

				if (typeof onUpdatePage === 'function') {
					await onUpdatePage(editOid, {
						Name: trimmedName,
						Description: newDescription.trim() || undefined,
						Icon: newIcon || undefined,
					});
				}

				if (typeof onUpdated === 'function') {
					await onUpdated(editOid);
				}

				closeForm();
			}
		} catch (error) {
			setCreateError(error instanceof Error ? error.message : 'Failed to save report page.');
		} finally {
			setCreating(false);
		}
	};

	return (
		<aside
			className={`h-full shrink-0 overflow-visible transition-[width] duration-200 ${isOpen ? 'w-72' : 'w-16'}`}
			aria-label="Report Side Menu"
		>
			<div className="h-full overflow-hidden rounded-none border-r border-slate-200 bg-white shadow-sm">
				<div className="flex w-full items-center justify-between border-b border-slate-200 px-3 py-3">
					<span className={`text-sm font-semibold tracking-tight text-slate-800 ${isOpen ? 'inline' : 'hidden'}`}>
						Report Pages
					</span>
					<div className="flex items-center gap-2">
						{isOpen ? (
							<button
								type="button"
								title="New report page"
								onClick={openCreateForm}
								className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 text-base leading-none text-slate-700 transition hover:bg-slate-100"
							>
								+
							</button>
						) : null}
						<button
							type="button"
							title={isOpen ? 'Collapse menu' : 'Expand menu'}
							onClick={() => setIsOpen((prev) => !prev)}
							className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-slate-300 text-[11px] font-semibold text-slate-500 transition hover:bg-slate-100"
						>
							{isOpen ? '<<' : '>>'}
						</button>
					</div>
				</div>

				<div className="max-h-[calc(100vh-180px)] overflow-y-auto">
					{loading ? <p className="px-4 py-3 text-sm text-slate-500">Loading pages...</p> : null}
					{loadError ? <p className="px-4 py-3 text-sm text-red-700">{loadError}</p> : null}
					{!loading && !loadError && pages.length === 0 ? (
						<p className="px-4 py-3 text-sm text-slate-500">No report pages found.</p>
					) : null}

					{!loading && !loadError
						? pages.map((page) => {
								const isActive = !!page.Oid && page.Oid === activeReportPageOid;
								const menuIcon = getWidgetIconById(page.Icon);
								const MenuIcon = menuIcon?.Icon;

								return (
									<div
										key={page.Oid || page.Name || Math.random()}
										onMouseEnter={(event) => showCollapsedTooltip(event.currentTarget, page.Name)}
										onMouseLeave={hideCollapsedTooltip}
										onClick={() => {
											if (page.Oid) onSelect(page.Oid);
										}}
										className={`border-b border-slate-200 text-sm transition ${
											isActive
												? 'bg-sky-50 font-medium text-sky-900'
												: 'bg-white text-slate-700 hover:bg-slate-50'
										} ${isOpen ? 'px-3 py-2.5' : 'px-2 py-2.5'} ${page.Oid ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
									>
										<div className={`inline-flex w-full items-center ${isOpen ? 'justify-between gap-2' : 'justify-center'}`}>
											<button
												type="button"
												disabled={!page.Oid}
												onClick={() => {
													if (page.Oid) onSelect(page.Oid);
												}}
												onFocus={(event) => showCollapsedTooltip(event.currentTarget, page.Name)}
												onBlur={hideCollapsedTooltip}
												title={isOpen ? page.Name : undefined}
												className={`inline-flex items-center gap-2 ${isOpen ? 'min-w-0 flex-1 text-left' : 'justify-center'} ${page.Oid ? 'cursor-pointer' : 'cursor-not-allowed'}`}
											>
												{MenuIcon ? <MenuIcon className="h-4 w-4 shrink-0 text-slate-600" /> : null}
												<span className={`${isOpen ? 'block truncate' : 'hidden'}`}>{page.Name || 'Untitled report'}</span>
											</button>

											{isOpen ? (
												<button
													type="button"
													title="Edit report page"
													onClick={(event) => {
														event.stopPropagation();
														openEditForm(page);
													}}
													className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border border-slate-300 text-slate-500 transition hover:bg-slate-100"
												>
													<svg
														width="12"
														height="12"
														viewBox="0 0 24 24"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
														aria-hidden="true"
													>
														<path d="M3 17.25V21h3.75L19.81 7.94l-3.75-3.75L3 17.25z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
														<path d="M14.06 4.19l3.75 3.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
													</svg>
												</button>
											) : null}
										</div>
									</div>
								);
						  })
						: null}
				</div>
			</div>

			{!isOpen && hoveredReportTooltip ? (
				<div
					className="pointer-events-none fixed z-[400000] -translate-x-1/2 -translate-y-full rounded-md border border-slate-300 bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg"
					style={{ left: hoveredReportTooltip.x, top: hoveredReportTooltip.y }}
				>
					{hoveredReportTooltip.text}
				</div>
			) : null}

			{formMode ? (
				<div className="fixed inset-0 z-[300000] flex items-center justify-center px-4">
					<div className="absolute inset-0 bg-black/40" onClick={closeForm} />
					<div className="relative w-[min(520px,96%)] rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
						<div className="mb-3 flex items-center justify-between">
							<h3 className="text-base font-semibold tracking-tight text-slate-800">
								{formMode === 'create' ? 'Add Report Page' : 'Edit Report Page'}
							</h3>
							<button
								type="button"
								onClick={closeForm}
								className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 text-slate-500 transition hover:bg-slate-100"
								title="Close"
							>
								x
							</button>
						</div>

						<form className="space-y-3" onSubmit={handleCreateOrUpdateReportPage}>
							<input
								type="text"
								value={newName}
								onChange={(event) => setNewName(event.target.value)}
								placeholder="Name"
								className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
								required
							/>

							<input
								type="text"
								value={newDescription}
								onChange={(event) => setNewDescription(event.target.value)}
								placeholder="Description"
								className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
							/>

							<div className="relative">
								<button
									type="button"
									onClick={() => setIconPickerOpen((value) => !value)}
									className="inline-flex h-11 w-full items-center justify-between rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none transition hover:bg-slate-50"
								>
									<span className="inline-flex items-center gap-2">
										{getWidgetIconById(newIcon)?.Icon ? (
											(() => {
												const Selected = getWidgetIconById(newIcon)?.Icon;
												return Selected ? <Selected className="h-4 w-4 text-slate-700" /> : null;
											})()
										) : (
											<span className="text-slate-500">No icon</span>
										)}
										{newIcon ? <span>{getWidgetIconById(newIcon)?.label}</span> : null}
									</span>
									<span className="text-slate-500">▾</span>
								</button>

								{iconPickerOpen ? (
									<div className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
										<button
											type="button"
											className="w-full rounded-lg px-2 py-2 text-left text-sm transition hover:bg-slate-100"
											onClick={() => {
												setNewIcon('');
												setIconPickerOpen(false);
											}}
										>
											No icon
										</button>

										{widgetIconOptions.map((iconOption) => {
											const Icon = iconOption.Icon;
											return (
												<button
													key={iconOption.id}
													type="button"
													className={`inline-flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition hover:bg-slate-100 ${newIcon === iconOption.id ? 'bg-slate-100' : ''}`}
													onClick={() => {
														setNewIcon(iconOption.id);
														setIconPickerOpen(false);
													}}
												>
													<Icon className="h-4 w-4 text-slate-700" />
													<span>{iconOption.label}</span>
												</button>
											);
										})}
									</div>
								) : null}
							</div>

							{formMode === 'create' ? (
								<select
									value={templateOid}
									onChange={(event) => setTemplateOid(event.target.value)}
									className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
								>
									<option value="">No template (blank page)</option>
									{pages
										.filter((page) => !!page.Oid)
										.map((page) => (
											<option key={page.Oid} value={page.Oid}>
												{page.Name || page.Oid}
											</option>
										))}
								</select>
							) : null}

							{createError ? <p className="text-xs text-red-700">{createError}</p> : null}

							<div className="mt-3 flex justify-end gap-2">
								<button
									type="button"
									onClick={closeForm}
									className="rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
								>
									Cancel
								</button>
								<button
									type="submit"
									disabled={creating || !newName.trim()}
									className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-60"
								>
									{creating
										? formMode === 'create'
											? 'Creating...'
											: 'Saving...'
										: formMode === 'create'
											? 'Create Report Page'
											: 'Save Changes'}
								</button>
							</div>
						</form>
					</div>
				</div>
			) : null}
		</aside>
	);
}

export default ReportSideMenu;
